import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RoutingSkuTargetRecord } from '@nadoutong/contracts';
import { listUpstreamCapabilities } from '../../api/upstreams.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { LIVE_CAPABILITY_STATUSES, capabilityStatusLabel } from '../../pages/capabilityStatus.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Dialog, FormField, Select, Skeleton } from '../../ui/index.ts';
import { coerceErrorMessage } from '../Toast.tsx';

/**
 * Pin a capability onto a legacy (`capability_id IS NULL`) target — #1428.
 *
 * `parsePutTarget` requires a non-empty `capability_id`, so a pre-0086 row
 * cannot be saved in place at all: its weight, retry order and enabled flag
 * are frozen until it carries a pin. This dialog is the unfreeze, and it is
 * deliberately narrow — the four-tuple is ALREADY decided by the existing
 * row, so the only choice offered is which capability under that exact
 * upstream / account / key bucket / upstream channel the row meant. Letting
 * the operator re-pick the four-tuple here would be a re-bind wearing a
 * repair's clothes, and the manual add-target dialog already does that job.
 *
 * One row at a time, never a batch: each legacy row is a separate judgement
 * about which lane it was serving, and a bulk "pin them all" would guess.
 */

export function PinCapabilityDialog({
  target,
  skuName,
  capabilityIdsInUse,
  initialCapabilityId,
  saving,
  onClose,
  onConfirm,
}: {
  readonly target: RoutingSkuTargetRecord;
  readonly skuName: string;
  /**
   * Capabilities this SKU's other targets already pin. Pinning onto one of
   * them is a silent merge, not a repair: `upsertSkuTarget` finds the sibling
   * row first (`ORDER BY capability_id IS NULL ASC`), overwrites ITS weight
   * with this row's, and the cleanup DELETE then removes this row. Two rows
   * become one and the survivor's weight changes — under a "capability
   * pinned" toast. So they are not offered.
   */
  readonly capabilityIdsInUse: ReadonlySet<string>;
  /**
   * Capability to pre-select — the one the operator clicked "bind" on before
   * being diverted here. Null when the pin was started from the row itself,
   * where there is no such intent to carry over.
   */
  readonly initialCapabilityId: string | null;
  readonly saving: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (capabilityId: string) => void;
}): JSX.Element {
  const t = useT();
  const { fetchImpl } = useSession();
  const [capabilityId, setCapabilityId] = useState(initialCapabilityId ?? '');

  const capabilities = useQuery({
    queryKey: ['tenant-routing', 'pin-capabilities', target.upstream_id] as const,
    queryFn: ({ signal }) => listUpstreamCapabilities(target.upstream_id, fetchImpl, signal),
  });

  /**
   * Live capabilities under this row's four-tuple, BEFORE the in-use
   * exclusion. Kept separate so the empty state can tell the two causes apart:
   * "this lane has no live capability" and "they are all already pinned" want
   * opposite next actions from the operator.
   */
  const liveUnderTuple = useMemo(
    () => (capabilities.data ?? []).filter((capability) =>
      capability.upstream_id === target.upstream_id &&
      capability.account_id === target.account_id &&
      capability.key_bucket_id === target.key_bucket_id &&
      capability.channel_id === target.upstream_channel_id &&
      LIVE_CAPABILITY_STATUSES.has(capability.status)),
    [capabilities.data, target],
  );
  const options = useMemo(
    () => liveUnderTuple.filter((capability) => !capabilityIdsInUse.has(capability.id)),
    [capabilityIdsInUse, liveUnderTuple],
  );
  /**
   * The pin actually on offer: a draft id counts only while it is a MEMBER of
   * `options`. Membership, not non-emptiness, is the gate.
   *
   * `capabilityId` starts seeded from the diverted bind, so it is non-empty on
   * the first frame — before the capability read resolves, and regardless of
   * whether it fails or returns nothing offerable. Gating on length alone
   * would leave the confirm button live under all three states in which this
   * dialog has just said it has nothing to give: pending (skeleton showing),
   * error (「could not load」 + retry showing), and all-taken (「delete this
   * duplicate row」 showing). `options` is `[]` in every one of them, so this
   * single check closes all three.
   */
  const selected = options.some((capability) => capability.id === capabilityId) ? capabilityId : '';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('sku_detail.supply.pin_title')}
      description={`${skuName} · ${target.upstream_channel_slug}`}
      closeLabel={t('common.close')}
      testId="sku-supply-pin-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={saving || selected.length === 0}
            loading={saving}
            onClick={() => onConfirm(selected)}
            data-testid="sku-supply-pin-confirm"
          >
            {t('sku_detail.supply.pin_confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/*
          The Select comes FIRST on purpose. Radix opens its popper downward
          against the viewport, not against the dialog, so as the last element
          in a short dialog its option list covered the cancel / confirm
          buttons. Above the explanatory copy it opens over text instead — and
          it is the decision this dialog exists for, so it reads first too.
        */}
        {capabilities.isPending ? (
          <Skeleton className="h-9 w-full" />
        ) : capabilities.isError ? (
          /*
            A failed read is not a supply verdict. Collapsing it into "no live
            capability exists here" would send the operator to re-probe healthy
            supply — or to delete the row as dead — off a transport error. The
            panel distinguishes read failure from empty answer everywhere else
            (the 503 / 404 / not-synced tri-state); so does this.
          */
          <div className="flex flex-col items-start gap-2" role="alert" data-testid="sku-supply-pin-error">
            <p className="m-0 text-dense leading-5 text-bad">
              {t('sku_detail.supply.pin_load_failed')}
            </p>
            <p className="m-0 text-micro text-paper-low">{coerceErrorMessage(capabilities.error)}</p>
            <Button
              size="sm"
              disabled={capabilities.isFetching}
              onClick={() => {
                void capabilities.refetch();
              }}
              data-testid="sku-supply-pin-retry"
            >
              {t('common.retry')}
            </Button>
          </div>
        ) : options.length === 0 ? (
          /*
            Two causes, opposite remedies, so they never share a sentence.
            Nothing live under the four-tuple means go probe the lane. Live
            capabilities that a sibling target already pins means this unpinned
            row is a DUPLICATE of one that already works — telling that
            operator to re-probe would send them to fix healthy supply while
            the actual fix (delete this row) goes unmentioned.
          */
          liveUnderTuple.length > 0 ? (
            <p className="m-0 text-dense leading-5 text-warn" data-testid="sku-supply-pin-all-taken">
              {t('sku_detail.supply.pin_all_taken')}
            </p>
          ) : (
            <p className="m-0 text-dense leading-5 text-warn" data-testid="sku-supply-pin-empty">
              {t('sku_detail.supply.pin_empty')}
            </p>
          )
        ) : (
          <FormField
            label={t('tenant_routing.default.capability')}
            fieldId="sku-supply-pin-capability"
            hint={t('sku_detail.supply.pin_select_hint')}
          >
            <Select
              id="sku-supply-pin-capability"
              ariaLabel={t('tenant_routing.default.capability')}
              placeholder={t('sku_detail.supply.pin_select_placeholder')}
              value={selected.length > 0 ? selected : undefined}
              onValueChange={setCapabilityId}
              options={options.map((capability) => ({
                value: capability.id,
                label: `${capability.upstream_model_name} · ${capability.id} · ${capability.probe_protocol ?? capability.protocol} · ${capabilityStatusLabel(capability.status, t)}`,
              }))}
              testId="sku-supply-pin-capability"
            />
          </FormField>
        )}
        <p className="m-0 text-dense leading-5 text-paper-low">{t('sku_detail.supply.pin_hint')}</p>
        <p className="m-0 rounded-lg border border-line-soft bg-well p-3 font-mono text-micro text-paper-low">
          {target.upstream_id} · {target.account_id} · {target.key_bucket_id} ·{' '}
          {target.upstream_channel_slug}
        </p>
      </div>
    </Dialog>
  );
}
